/**
 * Fase 1 Blokk 5 T1.16 — Pågående kompositt-vurderinger for kandidat
 *
 * Vises på Dashboard når kandidat har aktive kompositt-vurderinger som ikke er
 * finalisert. Widget-en henter fra GET /api/candidate/pending-assessments og
 * viser per plan: kurs, komponent-status, fremdrift, neste-frist.
 *
 * Widget skjules stille hvis endepunkt returnerer count=0 eller ved feil.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Clock, Circle, CheckCircle, Layers, AlertTriangle, ChevronRight, Info } from 'lucide-react';

interface Component {
  component_id: string;
  component_type: string;
  title: string;
  sequence: number;
  deadline: string | null;
  weight_pct: number | null;
  is_required: boolean;
  submission_id: string | null;
  submission_status: string;
  submitted_at: string | null;
  sensor_graded_at: string | null;
}

// Komponent-typer som har kandidat-opplasting via SubmissionUploadPage
const UPLOADABLE_TYPES = new Set(['project', 'portfolio', 'group']);
// Komponent-typer som krever fysisk oppmøte (sensor registrerer direkte)
const ATTENDANCE_TYPES = new Set(['oral', 'practical', 'observation']);

interface Plan {
  candidate_id: string;
  candidate_code: string;
  course_instance_id: string;
  course_name: string;
  host_organization: string | null;
  exam_window_end: string | null;
  plan_id: string;
  plan_model: string;
  final_status: string | null;
  overall_grade: string | null;
  overall_passed: boolean | null;
  calculated_score: number | null;
  total_components: number;
  submitted_count: number;
  graded_count: number;
  components: Component[];
}

interface Response {
  count: number;
  plans: Plan[];
}

const COMPONENT_TYPE_LABELS: Record<string, string> = {
  written: 'Skriftlig prøve',
  project: 'Prosjektinnlevering',
  oral: 'Muntlig',
  practical: 'Praktisk',
  portfolio: 'Mappe',
  observation: 'Observasjon',
  reflection: 'Refleksjonsnotat',
  group: 'Gruppearbeid'
};

const STATUS_LABELS: Record<string, string> = {
  not_started: 'Ikke påbegynt',
  in_progress: 'Under arbeid',
  submitted: 'Levert',
  graded: 'Sensurert',
  overridden_by_alternative: 'Alternativ vurdering'
};

function statusIcon(status: string) {
  if (status === 'graded' || status === 'overridden_by_alternative') {
    return <CheckCircle className="h-4 w-4 text-emerald-600" />;
  }
  if (status === 'submitted') {
    return <Clock className="h-4 w-4 text-kf-blue-600" />;
  }
  if (status === 'in_progress') {
    return <Circle className="h-4 w-4 text-kf-orange-500" />;
  }
  return <Circle className="h-4 w-4 text-gray-400" />;
}

function formatDate(iso: string | null) {
  if (!iso) return null;
  return new Date(iso).toLocaleDateString('no-NO', { day: '2-digit', month: 'short', year: 'numeric' });
}

export function PendingAssessmentsWidget() {
  const [data, setData] = useState<Response | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch('/api/candidate/pending-assessments', { credentials: 'include' });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        if (!cancelled) { setData(json); setLoading(false); }
      } catch {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading || !data || data.count === 0) return null;

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
      <div className="flex items-center gap-2 mb-4">
        <div className="p-2 bg-purple-50 rounded-lg">
          <Layers className="h-5 w-5 text-purple-700" />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Pågående vurderinger</h2>
          <p className="text-sm text-gray-600">
            Du har {data.count} {data.count === 1 ? 'vurdering' : 'vurderinger'} med flere deler
          </p>
        </div>
      </div>

      <div className="space-y-4">
        {data.plans.map(plan => {
          const pct = plan.total_components > 0
            ? Math.round((plan.submitted_count / plan.total_components) * 100)
            : 0;
          const anyDeadlineSoon = plan.exam_window_end &&
            new Date(plan.exam_window_end).getTime() - Date.now() < 7 * 24 * 60 * 60 * 1000;

          return (
            <div key={plan.plan_id} className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-start justify-between gap-4 mb-3">
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900">{plan.course_name}</h3>
                  {plan.host_organization && (
                    <p className="text-xs text-gray-500">Arrangør: {plan.host_organization}</p>
                  )}
                </div>
                {plan.exam_window_end && (
                  <div className={`text-xs px-2 py-1 rounded ${anyDeadlineSoon ? 'bg-kf-orange-50 text-kf-orange-700' : 'bg-gray-100 text-gray-600'}`}>
                    {anyDeadlineSoon && <AlertTriangle className="inline h-3 w-3 mr-1" />}
                    Frist: {formatDate(plan.exam_window_end)}
                  </div>
                )}
              </div>

              <div className="mb-3">
                <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
                  <span>Fremdrift: {plan.submitted_count}/{plan.total_components} levert · {plan.graded_count} sensurert</span>
                  <span className="font-medium">{pct}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-purple-600 h-2 rounded-full transition-all"
                    style={{ width: `${pct}%` }}
                  />
                </div>
              </div>

              <ul className="space-y-1 mb-3">
                {plan.components.map(c => {
                  return (
                    <li key={c.component_id} className="flex items-center gap-2 text-sm">
                      {statusIcon(c.submission_status)}
                      <span className="flex-1">
                        <span className="text-gray-900">{c.title || COMPONENT_TYPE_LABELS[c.component_type] || c.component_type}</span>
                        {c.weight_pct != null && <span className="text-xs text-gray-500 ml-2">({c.weight_pct}%)</span>}
                      </span>
                      <span className="text-xs text-gray-600">{STATUS_LABELS[c.submission_status] || c.submission_status}</span>
                      {c.deadline && c.submission_status !== 'graded' && c.submission_status !== 'submitted' && (
                        <span className="text-xs text-gray-500">Frist: {formatDate(c.deadline)}</span>
                      )}
                    </li>
                  );
                })}
              </ul>

              {(() => {
                // Fase 2.5 T2.5.3 — CTA per komponent når kandidat kan starte neste steg
                // Prioriter uploadable komponenter (project/portfolio/group) først siden de har klar handling,
                // deretter oppmøte-komponenter (oral/practical/observation), deretter andre (reflection).
                const uploadable = plan.components.find(c =>
                  UPLOADABLE_TYPES.has(c.component_type)
                  && (c.submission_status === 'not_started' || c.submission_status === 'in_progress')
                );
                const attendance = plan.components.find(c =>
                  ATTENDANCE_TYPES.has(c.component_type)
                  && (c.submission_status === 'not_started' || c.submission_status === 'in_progress')
                );
                const nextPending = uploadable || attendance
                  || plan.components.find(c => c.submission_status === 'not_started' || c.submission_status === 'in_progress');
                if (!nextPending) return null;

                const daysUntil = nextPending.deadline
                  ? Math.round((new Date(nextPending.deadline).getTime() - Date.now()) / 86400000)
                  : null;
                const deadlineLabel = daysUntil != null && daysUntil >= 0
                  ? (daysUntil === 0 ? 'Frist i dag' : `Frist om ${daysUntil} ${daysUntil === 1 ? 'dag' : 'dager'}`)
                  : null;

                // Klikkbar lenke for upload-typer når submission_id finnes
                if (UPLOADABLE_TYPES.has(nextPending.component_type) && nextPending.submission_id) {
                  const cta = nextPending.submission_status === 'in_progress' ? 'Fortsett innlevering' : 'Start innlevering';
                  return (
                    <Link
                      to={`/submissions/${nextPending.submission_id}`}
                      className="bg-kf-blue-50 hover:bg-kf-blue-100 border border-kf-blue-200 rounded p-3 text-sm text-kf-blue-900 flex items-center justify-between transition-colors group"
                    >
                      <div>
                        <span className="font-medium">Neste steg:</span>{' '}
                        {nextPending.title || COMPONENT_TYPE_LABELS[nextPending.component_type]}
                        {deadlineLabel && <span className="ml-2 text-xs text-kf-blue-700">{deadlineLabel}</span>}
                      </div>
                      <div className="flex items-center gap-2 text-kf-blue-700 font-medium">
                        <span>{cta}</span>
                        <ChevronRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" />
                      </div>
                    </Link>
                  );
                }

                // Oppmøte-typer: informativ boks uten lenke (sensor registrerer direkte)
                if (ATTENDANCE_TYPES.has(nextPending.component_type)) {
                  return (
                    <div className="bg-amber-50 border border-amber-200 rounded p-3 text-sm text-amber-900 flex items-start gap-2">
                      <Info className="h-4 w-4 mt-0.5 flex-shrink-0" />
                      <div>
                        <span className="font-medium">Neste steg:</span>{' '}
                        {nextPending.title || COMPONENT_TYPE_LABELS[nextPending.component_type]}
                        {deadlineLabel && <span className="ml-2 text-xs">{deadlineLabel}</span>}
                        <div className="text-xs mt-1">
                          Krever fysisk/digitalt oppmøte — se detaljer i invitasjonen fra kursarrangør.
                        </div>
                      </div>
                    </div>
                  );
                }

                // Fallback (f.eks. reflection uten etablert upload-flyt): kun tekst
                return (
                  <div className="bg-kf-blue-50 border border-kf-blue-200 rounded p-2 text-sm text-kf-blue-900">
                    <span className="font-medium">Neste steg:</span>{' '}
                    {nextPending.title || COMPONENT_TYPE_LABELS[nextPending.component_type]}
                    {deadlineLabel && <span className="ml-2 text-xs text-kf-blue-700">{deadlineLabel}</span>}
                  </div>
                );
              })()}

              {plan.final_status && plan.final_status !== 'pending_components' && (
                <div className="mt-2 pt-2 border-t border-gray-100 text-xs text-gray-600">
                  Status: <span className="font-medium">{plan.final_status.replace(/_/g, ' ')}</span>
                  {plan.overall_grade && <> · Foreløpig karakter: <span className="font-medium">{plan.overall_grade}</span></>}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
